/*
 * Smart Search — layer di ricerca avanzata in stile Doofinder
 * Motore nativo Shopify (search/suggest.json + template alternativo smart-json)
 * Nessuna dipendenza esterna.
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SEARCH_URL = ROOT + 'search';
  var SUGGEST_URL = ROOT + 'search/suggest.json';
  var HISTORY_KEY = 'smart-search:history';
  var SEARCH_FIELDS = 'title,product_type,variants.title,vendor,variants.sku,tag';

  /* ------------------------------------------------------------------ utils */

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegex(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function normalize(str) {
    return String(str == null ? '' : str)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenize(term) {
    return normalize(term)
      .split(' ')
      .filter(function (t) {
        return t.length > 1;
      });
  }

  function highlight(text, tokens) {
    var safe = escapeHtml(text);
    if (!tokens || !tokens.length) return safe;
    var pattern = tokens
      .slice()
      .sort(function (a, b) {
        return b.length - a.length;
      })
      .map(escapeRegex)
      .join('|');
    try {
      var re = new RegExp('(' + pattern + ')', 'gi');
      return safe.replace(re, '<mark>$1</mark>');
    } catch (e) {
      return safe;
    }
  }

  function uniq(arr) {
    return arr.filter(function (v, i) {
      return arr.indexOf(v) === i;
    });
  }

  function readHistory() {
    try {
      var raw = window.localStorage.getItem(HISTORY_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function pushHistory(term) {
    if (!term) return;
    try {
      var list = readHistory().filter(function (t) {
        return normalize(t) !== normalize(term);
      });
      list.unshift(term);
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 8)));
    } catch (e) {
      /* storage non disponibile */
    }
  }

  function clearHistory() {
    try {
      window.localStorage.removeItem(HISTORY_KEY);
    } catch (e) {
      /* noop */
    }
  }

  function buildSearchUrl(term, page, view) {
    var params = [
      'q=' + encodeURIComponent(term),
      'type=product',
      'options%5Bprefix%5D=last',
      'options%5Bfields%5D=' + encodeURIComponent(SEARCH_FIELDS)
    ];
    if (view) params.push('view=' + view);
    if (page && page > 1) params.push('page=' + page);
    return SEARCH_URL + '?' + params.join('&');
  }

  function publicSearchUrl(term) {
    return buildSearchUrl(term, 1, null);
  }

  /* ------------------------------------------------------- parsing settings */

  function parseSynonyms(raw) {
    if (!raw) return [];
    return String(raw)
      .split(/\r?\n/)
      .map(function (line) {
        var parts = line.split(/=>|;/);
        if (parts.length < 2) return null;
        var from = parts[0]
          .split(',')
          .map(function (s) {
            return normalize(s);
          })
          .filter(Boolean);
        var to = parts[1].trim();
        if (!from.length || !to) return null;
        return { from: from, to: to };
      })
      .filter(Boolean);
  }

  function parseRedirects(raw) {
    if (!raw) return [];
    return String(raw)
      .split(/\r?\n/)
      .map(function (line) {
        var parts = line.split(/=>|;/);
        if (parts.length < 2) return null;
        var terms = parts[0]
          .split(',')
          .map(function (s) {
            return normalize(s);
          })
          .filter(Boolean);
        var url = parts[1].trim();
        if (!terms.length || !url) return null;
        return { terms: terms, url: url };
      })
      .filter(Boolean);
  }

  /* ------------------------------------------------------------ the element */

  class SmartSearchDrawer extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.init();
    }

    init() {
      this.cfg = {};
      try {
        this.cfg = JSON.parse(this.getAttribute('data-config') || '{}');
      } catch (e) {
        this.cfg = {};
      }

      this.cfg.minChars = parseInt(this.cfg.minChars, 10) || 2;
      this.cfg.perLoad = parseInt(this.cfg.perLoad, 10) || 24;
      this.cfg.maxPages = parseInt(this.cfg.maxPages, 10) || 3;
      this.labels = this.cfg.labels || {};
      this.synonyms = parseSynonyms(this.cfg.synonymsRaw);
      this.redirects = parseRedirects(this.cfg.redirectsRaw);
      this.banners = this.cfg.banners || [];

      this.cache = {};
      this.state = {
        term: '',
        appliedTerm: '',
        correctedFrom: '',
        products: [],
        totalPages: 1,
        fetchedPages: 0,
        resultsCount: 0,
        approximate: false,
        filters: { availability: [], vendor: [], type: [], collection: [], tag: [], sale: [], price: null },
        sort: 'relevance',
        visible: 0,
        groups: { queries: [], collections: [], pages: [], articles: [] },
        loading: false,
        showAllTags: false,
        expandedFacets: {}
      };

      this.controller = null;
      this.isOpen = false;

      this.dom = {
        panel: this.querySelector('[data-ss-panel]'),
        backdrop: this.querySelector('[data-ss-backdrop]'),
        input: this.querySelector('[data-ss-input]'),
        form: this.querySelector('[data-ss-form]'),
        clear: this.querySelector('[data-ss-clear]'),
        close: this.querySelector('[data-ss-close]'),
        body: this.querySelector('[data-ss-body]'),
        idle: this.querySelector('[data-ss-idle]'),
        results: this.querySelector('[data-ss-results]'),
        facets: this.querySelector('[data-ss-facets]'),
        main: this.querySelector('[data-ss-main]'),
        toolbar: this.querySelector('[data-ss-toolbar]'),
        grid: this.querySelector('[data-ss-grid]'),
        banner: this.querySelector('[data-ss-banner]'),
        groups: this.querySelector('[data-ss-groups]'),
        footer: this.querySelector('[data-ss-footer]'),
        status: this.querySelector('[data-ss-status]'),
        filtersToggle: this.querySelector('[data-ss-filters-toggle]'),
        activeChips: this.querySelector('[data-ss-active-filters]'),
        toast: this.querySelector('[data-ss-toast]')
      };

      this.onInput = debounce(this.handleInput.bind(this), parseInt(this.cfg.debounce, 10) || 250);
      this.bindEvents();
      this.bindTriggers();
      this.renderIdle();
    }

    /* ------------------------------------------------------------ binding */

    bindEvents() {
      var self = this;

      if (this.dom.input) {
        this.dom.input.addEventListener('input', function () {
          self.toggleClear();
          self.onInput();
        });
        this.dom.input.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') self.close();
        });
      }

      if (this.dom.form) {
        this.dom.form.addEventListener('submit', function (e) {
          e.preventDefault();
          var term = (self.dom.input.value || '').trim();
          if (!term) return;
          var redirect = self.matchRedirect(term);
          pushHistory(term);
          window.location.href = redirect ? redirect.url : publicSearchUrl(term);
        });
      }

      if (this.dom.clear) {
        this.dom.clear.addEventListener('click', function () {
          self.dom.input.value = '';
          self.toggleClear();
          self.reset();
          self.dom.input.focus();
        });
      }

      if (this.dom.close) {
        this.dom.close.addEventListener('click', function () {
          self.close();
        });
      }

      if (this.dom.backdrop) {
        this.dom.backdrop.addEventListener('click', function () {
          self.close();
        });
      }

      if (this.dom.filtersToggle) {
        this.dom.filtersToggle.addEventListener('click', function () {
          self.classList.toggle('smart-search--facets-open');
        });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) self.close();
      });

      /* delega eventi su tutto il pannello */
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
    }

    bindTriggers() {
      var self = this;

      function openFrom(el, e) {
        e.preventDefault();
        e.stopPropagation();
        var seed = '';
        var input = el.tagName === 'INPUT' ? el : null;
        if (input) seed = input.value || '';
        self.open(seed);
      }

      /* 1. elementi che dichiarano esplicitamente il trigger */
      document.querySelectorAll('[data-smart-search-open]').forEach(function (el) {
        el.addEventListener('click', function (e) {
          openFrom(el, e);
        });
      });

      /* 2. sostituisce i punti di ricerca nativi del tema */
      if (this.cfg.replaceThemeSearch !== false) {
        var triggerSelectors = [
          '.header__icon--search',
          '.header__search summary',
          '[data-header-search-toggle]'
        ].join(',');

        var inputSelectors = ['input[name="q"]', '.search__input'].join(',');

        var nodes = [].slice.call(document.querySelectorAll(triggerSelectors + ',' + inputSelectors));

        nodes
          .filter(function (el) {
            /* esclude il layer stesso e il form di ricerca della pagina
               /search, che deve restare utilizzabile normalmente */
            return !el.closest('smart-search') && !el.closest('#MainContent');
          })
          .forEach(function (el) {
            el.addEventListener(
              'click',
              function (e) {
                openFrom(el, e);
              },
              true
            );
            if (el.tagName === 'INPUT') {
              el.setAttribute('readonly', 'readonly');
              el.addEventListener('focus', function (e) {
                openFrom(el, e);
                el.blur();
              });
            }
          });
      }

      /* 3. apertura via hash o querystring ?smart-search=term */
      var params = new URLSearchParams(window.location.search);
      if (params.has('smart-search')) {
        setTimeout(function () {
          self.open(params.get('smart-search') || '');
        }, 200);
      }
    }

    /* ------------------------------------------------------- open / close */

    open(seed) {
      if (this.isOpen) {
        this.dom.input.focus();
        return;
      }
      this.isOpen = true;
      this.classList.add('is-open');
      document.documentElement.classList.add('smart-search-open');
      document.body.classList.add('smart-search-open');
      this.setAttribute('aria-hidden', 'false');

      var self = this;
      setTimeout(function () {
        self.dom.input.focus();
        if (seed) {
          self.dom.input.value = seed;
          self.toggleClear();
          self.handleInput();
        }
      }, 60);

      this.dispatchEvent(new CustomEvent('smart-search:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open', 'smart-search--facets-open');
      document.documentElement.classList.remove('smart-search-open');
      document.body.classList.remove('smart-search-open');
      this.setAttribute('aria-hidden', 'true');
      this.dispatchEvent(new CustomEvent('smart-search:close', { bubbles: true }));
    }

    reset() {
      this.state.term = '';
      this.state.appliedTerm = '';
      this.state.correctedFrom = '';
      this.state.products = [];
      this.state.groups = { queries: [], collections: [], pages: [], articles: [] };
      this.resetFilters();
      this.dom.results.hidden = true;
      this.dom.idle.hidden = false;
      this.renderIdle();
    }

    resetFilters() {
      this.state.filters = {
        availability: [],
        vendor: [],
        type: [],
        collection: [],
        tag: [],
        sale: [],
        price: null
      };
      this.state.visible = this.cfg.perLoad;
    }

    toggleClear() {
      if (!this.dom.clear) return;
      this.dom.clear.hidden = !(this.dom.input.value || '').length;
    }

    /* ------------------------------------------------------------ ricerca */

    handleInput() {
      var term = (this.dom.input.value || '').trim();
      this.state.term = term;

      if (term.length < this.cfg.minChars) {
        this.dom.results.hidden = true;
        this.dom.idle.hidden = false;
        this.renderIdle();
        return;
      }
      this.search(term);
    }

    applySynonym(term) {
      var n = normalize(term);
      for (var i = 0; i < this.synonyms.length; i++) {
        if (this.synonyms[i].from.indexOf(n) !== -1) return this.synonyms[i].to;
      }
      return term;
    }

    matchRedirect(term) {
      var n = normalize(term);
      for (var i = 0; i < this.redirects.length; i++) {
        if (this.redirects[i].terms.indexOf(n) !== -1) return this.redirects[i];
      }
      return null;
    }

    matchBanner(term) {
      var n = normalize(term);
      for (var i = 0; i < this.banners.length; i++) {
        var b = this.banners[i];
        var keys = (b.keywords || '')
          .split(',')
          .map(function (k) {
            return normalize(k);
          })
          .filter(Boolean);
        if (!keys.length) continue;
        for (var j = 0; j < keys.length; j++) {
          /* corrisponde se la query contiene la keyword oppure, mentre si
             digita, se la keyword inizia con la query (min 3 caratteri) */
          if (n === keys[j] || n.indexOf(keys[j]) !== -1 || (n.length >= 3 && keys[j].indexOf(n) === 0)) {
            return b;
          }
        }
      }
      return null;
    }

    async search(rawTerm) {
      var term = this.applySynonym(rawTerm);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      var signal = this.controller.signal;

      this.state.loading = true;
      this.dom.idle.hidden = true;
      this.dom.results.hidden = false;
      this.setStatus(this.labels.loading || 'Searching…');
      this.renderSkeleton();

      var self = this;
      var cacheKey = 'p:' + normalize(term);

      try {
        var data;
        if (this.cache[cacheKey]) {
          data = this.cache[cacheKey];
        } else {
          data = await this.fetchProducts(term, 1, signal);
          this.cache[cacheKey] = data;
        }

        var correctedFrom = '';

        /* tolleranza agli errori di battitura */
        if ((!data || !data.products.length) && this.cfg.typoTolerance !== false) {
          var suggestion = await this.findCorrection(term, signal);
          if (suggestion) {
            var corrected = await this.fetchProducts(suggestion, 1, signal);
            if (corrected && corrected.products.length) {
              correctedFrom = term;
              term = suggestion;
              data = corrected;
              this.cache['p:' + normalize(term)] = corrected;
            }
          }
        }

        if (signal.aborted) return;

        this.state.appliedTerm = term;
        this.state.correctedFrom = correctedFrom;
        this.state.products = (data && data.products) || [];
        this.state.resultsCount = (data && data.results_count) || this.state.products.length;
        this.state.totalPages = (data && data.total_pages) || 1;
        this.state.fetchedPages = 1;
        this.state.approximate = this.state.totalPages > this.cfg.maxPages;
        this.resetFilters();
        this.state.loading = false;

        this.render();

        /* gruppi (collezioni / pagine / articoli / query) in parallelo */
        if (this.cfg.groupsEnabled !== false) {
          this.fetchSuggest(term, signal)
            .then(function (groups) {
              if (signal.aborted) return;
              self.state.groups = groups;
              self.renderGroups();
            })
            .catch(function () {});
        }

        /* completa il set per facet piu' accurate */
        this.hydrateRemainingPages(term, signal);

        pushHistory(rawTerm);
        this.dispatchEvent(
          new CustomEvent('smart-search:search', {
            bubbles: true,
            detail: { term: term, results: this.state.resultsCount }
          })
        );
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        this.state.loading = false;
        this.dom.grid.innerHTML =
          '<p class="smart-search__error">' + escapeHtml(this.labels.error || 'Search is unavailable, please try again.') + '</p>';
      }
    }

    async hydrateRemainingPages(term, signal) {
      var maxPages = Math.min(this.state.totalPages, this.cfg.maxPages);
      if (maxPages < 2) return;
      for (var page = 2; page <= maxPages; page++) {
        try {
          var extra = await this.fetchProducts(term, page, signal);
          if (signal.aborted) return;
          if (extra && extra.products && extra.products.length) {
            this.state.products = this.state.products.concat(extra.products);
            this.state.fetchedPages = page;
            this.render();
          }
        } catch (e) {
          return;
        }
      }
    }

    async fetchProducts(term, page, signal) {
      var res = await fetch(buildSearchUrl(term, page, 'smart-json'), {
        signal: signal,
        headers: { Accept: 'text/html,application/json' }
      });
      if (!res.ok) throw new Error('search failed');
      var text = await res.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error('invalid json');
      }
    }

    async fetchSuggest(term, signal) {
      var url =
        SUGGEST_URL +
        '?q=' +
        encodeURIComponent(term) +
        '&resources[type]=query,collection,article,page' +
        '&resources[limit]=5&resources[options][unavailable_products]=last';
      var res = await fetch(url, { signal: signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('suggest failed');
      var json = await res.json();
      var r = (json.resources && json.resources.results) || {};
      return {
        queries: r.queries || [],
        collections: r.collections || [],
        pages: r.pages || [],
        articles: r.articles || []
      };
    }

    /* correzione errori di battitura: suggerimenti Shopify + varianti a
       distanza 1 (cancellazione / trasposizione) testate in parallelo */
    async findCorrection(term, signal) {
      var self = this;
      var n = normalize(term);
      if (n.length < 4) return null;

      try {
        var sug = await this.fetchSuggest(term, signal);
        if (sug.queries && sug.queries.length) {
          var q = sug.queries[0].text || '';
          if (q && normalize(q) !== n) return q;
        }
      } catch (e) {
        /* continua con le varianti */
      }

      var words = n.split(' ');
      var longestIndex = 0;
      words.forEach(function (w, i) {
        if (w.length > words[longestIndex].length) longestIndex = i;
      });
      var target = words[longestIndex];
      if (target.length < 4) return null;

      var variants = [];
      var i;
      for (i = 0; i < target.length && variants.length < 12; i++) {
        variants.push(target.slice(0, i) + target.slice(i + 1));
      }
      for (i = 0; i < target.length - 1 && variants.length < 20; i++) {
        variants.push(target.slice(0, i) + target[i + 1] + target[i] + target.slice(i + 2));
      }
      variants = uniq(variants).slice(0, 14);

      var probes = variants.map(function (v) {
        var candidateWords = words.slice();
        candidateWords[longestIndex] = v;
        var candidate = candidateWords.join(' ');
        return self
          .probeCount(candidate, signal)
          .then(function (count) {
            return { term: candidate, count: count };
          })
          .catch(function () {
            return { term: candidate, count: 0 };
          });
      });

      var results = await Promise.all(probes);
      results.sort(function (a, b) {
        return b.count - a.count;
      });
      return results.length && results[0].count > 0 ? results[0].term : null;
    }

    async probeCount(term, signal) {
      var url =
        SUGGEST_URL +
        '?q=' +
        encodeURIComponent(term) +
        '&resources[type]=product&resources[limit]=4' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=' +
        encodeURIComponent(SEARCH_FIELDS);
      var res = await fetch(url, { signal: signal, headers: { Accept: 'application/json' } });
      if (!res.ok) return 0;
      var json = await res.json();
      var products = (json.resources && json.resources.results && json.resources.results.products) || [];
      return products.length;
    }

    /* ------------------------------------------------------------- facets */

    filterProducts(products, skipKey) {
      var f = this.state.filters;
      return products.filter(function (p) {
        if (skipKey !== 'availability' && f.availability.length) {
          var wantIn = f.availability.indexOf('in') !== -1;
          var wantOut = f.availability.indexOf('out') !== -1;
          if (!((wantIn && p.available) || (wantOut && !p.available))) return false;
        }
        if (skipKey !== 'vendor' && f.vendor.length && f.vendor.indexOf(p.vendor) === -1) return false;
        if (skipKey !== 'type' && f.type.length && f.type.indexOf(p.type) === -1) return false;
        if (skipKey !== 'sale' && f.sale.length && !p.on_sale) return false;
        if (skipKey !== 'collection' && f.collection.length) {
          var handles = (p.collections || []).map(function (c) {
            return c.handle;
          });
          var hit = f.collection.some(function (h) {
            return handles.indexOf(h) !== -1;
          });
          if (!hit) return false;
        }
        if (skipKey !== 'tag' && f.tag.length) {
          var tagHit = f.tag.some(function (t) {
            return (p.tags || []).indexOf(t) !== -1;
          });
          if (!tagHit) return false;
        }
        if (skipKey !== 'price' && f.price) {
          if (f.price.min != null && p.price < f.price.min * 100) return false;
          if (f.price.max != null && p.price > f.price.max * 100) return false;
        }
        return true;
      });
    }

    buildFacets() {
      var self = this;
      var all = this.state.products;
      var facets = {};

      function countBy(list, getter) {
        var map = {};
        list.forEach(function (p) {
          var values = getter(p);
          if (!Array.isArray(values)) values = values ? [values] : [];
          uniq(values).forEach(function (v) {
            if (!v) return;
            map[v] = (map[v] || 0) + 1;
          });
        });
        return Object.keys(map)
          .map(function (k) {
            return { value: k, label: k, count: map[k] };
          })
          .sort(function (a, b) {
            return b.count - a.count || a.label.localeCompare(b.label);
          });
      }

      var forAvail = this.filterProducts(all, 'availability');
      facets.availability = [
        {
          value: 'in',
          label: this.labels.inStock || 'In stock',
          count: forAvail.filter(function (p) {
            return p.available;
          }).length
        },
        {
          value: 'out',
          label: this.labels.outStock || 'Sold out',
          count: forAvail.filter(function (p) {
            return !p.available;
          }).length
        }
      ].filter(function (o) {
        return o.count > 0;
      });

      facets.vendor = countBy(this.filterProducts(all, 'vendor'), function (p) {
        return p.vendor;
      });
      facets.type = countBy(this.filterProducts(all, 'type'), function (p) {
        return p.type;
      });
      facets.tag = countBy(this.filterProducts(all, 'tag'), function (p) {
        return (p.tags || []).filter(function (t) {
          return t.indexOf('__') !== 0 && t.indexOf('_') !== 0;
        });
      });

      var collSource = this.filterProducts(all, 'collection');
      var collMap = {};
      collSource.forEach(function (p) {
        (p.collections || []).forEach(function (c) {
          if (!collMap[c.handle]) collMap[c.handle] = { value: c.handle, label: c.title, count: 0 };
          collMap[c.handle].count += 1;
        });
      });
      facets.collection = Object.keys(collMap)
        .map(function (k) {
          return collMap[k];
        })
        .sort(function (a, b) {
          return b.count - a.count || a.label.localeCompare(b.label);
        });

      var saleSource = this.filterProducts(all, 'sale');
      var saleCount = saleSource.filter(function (p) {
        return p.on_sale;
      }).length;
      facets.sale = saleCount
        ? [{ value: 'sale', label: self.labels.onSale || 'On sale', count: saleCount }]
        : [];

      var priceSource = this.filterProducts(all, 'price');
      if (priceSource.length) {
        var prices = priceSource.map(function (p) {
          return p.price;
        });
        facets.price = {
          min: Math.floor(Math.min.apply(null, prices) / 100),
          max: Math.ceil(Math.max.apply(null, prices) / 100)
        };
      } else {
        facets.price = null;
      }

      return facets;
    }

    sortProducts(list) {
      var sorted = list.slice();
      switch (this.state.sort) {
        case 'price-asc':
          sorted.sort(function (a, b) {
            return a.price - b.price;
          });
          break;
        case 'price-desc':
          sorted.sort(function (a, b) {
            return b.price - a.price;
          });
          break;
        case 'title-asc':
          sorted.sort(function (a, b) {
            return a.title.localeCompare(b.title);
          });
          break;
        case 'newest':
          sorted.sort(function (a, b) {
            return parseInt(b.published_at, 10) - parseInt(a.published_at, 10);
          });
          break;
        case 'discount':
          sorted.sort(function (a, b) {
            return b.discount_percent - a.discount_percent;
          });
          break;
        default:
          break;
      }
      return sorted;
    }

    /* -------------------------------------------------------------- render */

    setStatus(text) {
      if (this.dom.status) this.dom.status.textContent = text;
    }

    renderSkeleton() {
      var cards = '';
      for (var i = 0; i < 8; i++) {
        cards +=
          '<div class="smart-card smart-card--skeleton"><div class="smart-card__media"></div>' +
          '<div class="smart-skel smart-skel--line"></div><div class="smart-skel smart-skel--line short"></div></div>';
      }
      this.dom.grid.innerHTML = cards;
      if (this.dom.facets) this.dom.facets.innerHTML = '';
      if (this.dom.toolbar) this.dom.toolbar.innerHTML = '';
      if (this.dom.footer) this.dom.footer.innerHTML = '';
    }

    renderIdle() {
      if (!this.dom.idle) return;
      var self = this;
      var html = '';
      var history = this.cfg.historyEnabled !== false ? readHistory() : [];

      if (history.length) {
        html +=
          '<div class="smart-search__block"><div class="smart-search__block-head">' +
          '<h3>' +
          escapeHtml(this.labels.history || 'Recent searches') +
          '</h3><button type="button" class="smart-search__link" data-ss-clear-history>' +
          escapeHtml(this.labels.clearHistory || 'Clear') +
          '</button></div><div class="smart-search__chips">' +
          history
            .map(function (t) {
              return '<button type="button" class="smart-chip" data-ss-term="' + escapeHtml(t) + '">' + escapeHtml(t) + '</button>';
            })
            .join('') +
          '</div></div>';
      }

      var popular = (this.cfg.popular || '')
        .split(',')
        .map(function (t) {
          return t.trim();
        })
        .filter(Boolean);

      if (popular.length) {
        html +=
          '<div class="smart-search__block"><div class="smart-search__block-head"><h3>' +
          escapeHtml(this.labels.popular || 'Popular searches') +
          '</h3></div><div class="smart-search__chips">' +
          popular
            .map(function (t) {
              return '<button type="button" class="smart-chip" data-ss-term="' + escapeHtml(t) + '">' + escapeHtml(t) + '</button>';
            })
            .join('') +
          '</div></div>';
      }

      if (this.cfg.idleHtml) {
        html += '<div class="smart-search__block smart-search__block--custom">' + this.cfg.idleHtml + '</div>';
      }

      if (!html) {
        html =
          '<div class="smart-search__empty-idle"><p>' +
          escapeHtml(this.labels.startTyping || 'Start typing to search the store.') +
          '</p></div>';
      }

      this.dom.idle.innerHTML = html;
      this.dom.idle.hidden = false;
      if (this.dom.results) this.dom.results.hidden = true;

      var clearBtn = this.dom.idle.querySelector('[data-ss-clear-history]');
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          clearHistory();
          self.renderIdle();
        });
      }
    }

    render() {
      var tokens = tokenize(this.state.appliedTerm);
      var facets = this.buildFacets();
      var filtered = this.filterProducts(this.state.products, null);
      var sorted = this.sortProducts(filtered);
      if (!this.state.visible) this.state.visible = this.cfg.perLoad;
      var slice = sorted.slice(0, this.state.visible);

      this.renderBanner();
      this.renderFacets(facets);
      this.renderToolbar(filtered.length);
      this.renderActiveChips();

      if (!slice.length) {
        this.dom.grid.innerHTML = this.emptyStateHtml();
      } else {
        this.dom.grid.innerHTML = slice
          .map(function (p) {
            return this.cardHtml(p, tokens);
          }, this)
          .join('');
      }

      this.renderFooter(sorted.length);
      this.setStatus(
        (this.labels.resultsFor || 'Results for') + ' "' + this.state.appliedTerm + '": ' + filtered.length
      );
    }

    emptyStateHtml() {
      var term = escapeHtml(this.state.appliedTerm);
      var suggestions = (this.state.groups.queries || [])
        .slice(0, 5)
        .map(function (q) {
          return '<button type="button" class="smart-chip" data-ss-term="' + escapeHtml(q.text) + '">' + escapeHtml(q.text) + '</button>';
        })
        .join('');
      var popular = (this.cfg.popular || '')
        .split(',')
        .map(function (t) {
          return t.trim();
        })
        .filter(Boolean)
        .map(function (t) {
          return '<button type="button" class="smart-chip" data-ss-term="' + escapeHtml(t) + '">' + escapeHtml(t) + '</button>';
        })
        .join('');

      return (
        '<div class="smart-search__no-results">' +
        '<svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>' +
        '<h3>' +
        escapeHtml(this.labels.noResultsTitle || 'No results') +
        '</h3><p>' +
        escapeHtml(this.labels.noResultsText || "We couldn't find any products for") +
        ' <strong>' +
        term +
        '</strong>.</p>' +
        (suggestions || popular
          ? '<div class="smart-search__chips">' + (suggestions || popular) + '</div>'
          : '') +
        '</div>'
      );
    }

    renderBanner() {
      if (!this.dom.banner) return;
      var b = this.matchBanner(this.state.appliedTerm);
      if (!b) {
        this.dom.banner.innerHTML = '';
        this.dom.banner.hidden = true;
        return;
      }
      this.dom.banner.hidden = false;
      this.dom.banner.innerHTML =
        '<a class="smart-banner" href="' +
        escapeHtml(b.url || '#') +
        '">' +
        (b.image ? '<img src="' + escapeHtml(b.image) + '" alt="' + escapeHtml(b.heading || '') + '" loading="lazy">' : '') +
        '<div class="smart-banner__body">' +
        (b.heading ? '<strong>' + escapeHtml(b.heading) + '</strong>' : '') +
        (b.text ? '<span>' + escapeHtml(b.text) + '</span>' : '') +
        (b.button ? '<span class="smart-banner__cta">' + escapeHtml(b.button) + '</span>' : '') +
        '</div></a>';
    }

    renderFacets(facets) {
      if (!this.dom.facets || this.cfg.showFacets === false) return;
      var self = this;
      var order = this.cfg.facetOrder || ['availability', 'price', 'sale', 'vendor', 'type', 'collection', 'tag'];
      var titles = {
        availability: this.labels.facetAvailability || 'Availability',
        price: this.labels.facetPrice || 'Price',
        sale: this.labels.facetSale || 'Promotions',
        vendor: this.labels.facetVendor || 'Brand',
        type: this.labels.facetType || 'Product type',
        collection: this.labels.facetCollection || 'Collection',
        tag: this.labels.facetTag || 'Tag'
      };

      var html = '<div class="smart-facets__head"><h3>' + escapeHtml(this.labels.filters || 'Filters') + '</h3>';
      if (this.hasActiveFilters()) {
        html +=
          '<button type="button" class="smart-search__link" data-ss-clear-filters>' +
          escapeHtml(this.labels.clearFilters || 'Clear') +
          '</button>';
      }
      html +=
        '<button type="button" class="smart-facets__close" data-ss-facets-close aria-label="' +
        escapeHtml(this.labels.close || 'Close') +
        '"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
      html += '</div>';

      order.forEach(function (key) {
        if (key === 'price') {
          if (!facets.price || facets.price.min === facets.price.max) return;
          var cur = self.state.filters.price || {};
          html +=
            '<details class="smart-facet" open><summary>' +
            escapeHtml(titles.price) +
            '</summary><div class="smart-facet__body smart-facet__price">' +
            '<label><span>' +
            escapeHtml(self.labels.priceFrom || 'Min') +
            '</span><input type="number" inputmode="numeric" data-ss-price="min" min="' +
            facets.price.min +
            '" max="' +
            facets.price.max +
            '" placeholder="' +
            facets.price.min +
            '" value="' +
            (cur.min != null ? cur.min : '') +
            '"></label>' +
            '<label><span>' +
            escapeHtml(self.labels.priceTo || 'Max') +
            '</span><input type="number" inputmode="numeric" data-ss-price="max" min="' +
            facets.price.min +
            '" max="' +
            facets.price.max +
            '" placeholder="' +
            facets.price.max +
            '" value="' +
            (cur.max != null ? cur.max : '') +
            '"></label>' +
            '<button type="button" class="smart-btn smart-btn--small" data-ss-price-apply>' +
            escapeHtml(self.labels.apply || 'Apply') +
            '</button>' +
            '</div></details>';
          return;
        }

        var options = facets[key] || [];
        if (!options.length) return;
        if (key === 'availability' && options.length < 2 && !self.state.filters.availability.length) return;

        var expanded = self.state.expandedFacets[key];
        var limit = expanded ? options.length : 8;
        var visible = options.slice(0, limit);

        html +=
          '<details class="smart-facet" open><summary>' +
          escapeHtml(titles[key] || key) +
          '</summary><div class="smart-facet__body">';

        visible.forEach(function (opt) {
          var checked = self.state.filters[key].indexOf(opt.value) !== -1;
          html +=
            '<label class="smart-facet__option' +
            (checked ? ' is-active' : '') +
            '"><input type="checkbox" data-ss-facet="' +
            key +
            '" value="' +
            escapeHtml(opt.value) +
            '"' +
            (checked ? ' checked' : '') +
            '><span class="smart-facet__label">' +
            escapeHtml(opt.label) +
            '</span><span class="smart-facet__count">' +
            opt.count +
            '</span></label>';
        });

        if (options.length > 8) {
          html +=
            '<button type="button" class="smart-search__link" data-ss-facet-more="' +
            key +
            '">' +
            escapeHtml(expanded ? self.labels.showLess || 'Show less' : (self.labels.showMore || 'Show all') + ' (' + options.length + ')') +
            '</button>';
        }

        html += '</div></details>';
      });

      html +=
        '<div class="smart-facets__apply"><button type="button" class="smart-btn" data-ss-facets-close>' +
        escapeHtml(this.labels.seeResults || 'See results') +
        '</button></div>';

      this.dom.facets.innerHTML = html;
    }

    hasActiveFilters() {
      var f = this.state.filters;
      return (
        f.availability.length ||
        f.vendor.length ||
        f.type.length ||
        f.collection.length ||
        f.tag.length ||
        f.sale.length ||
        f.price
      );
    }

    renderActiveChips() {
      if (!this.dom.activeChips) return;
      var self = this;
      var f = this.state.filters;
      var chips = [];

      ['availability', 'vendor', 'type', 'collection', 'tag', 'sale'].forEach(function (key) {
        f[key].forEach(function (v) {
          var label = v;
          if (key === 'availability') label = v === 'in' ? self.labels.inStock || 'In stock' : self.labels.outStock || 'Sold out';
          if (key === 'sale') label = self.labels.onSale || 'On sale';
          if (key === 'collection') {
            var found = null;
            self.state.products.forEach(function (p) {
              (p.collections || []).forEach(function (c) {
                if (c.handle === v) found = c.title;
              });
            });
            label = found || v;
          }
          chips.push(
            '<button type="button" class="smart-chip smart-chip--active" data-ss-remove-filter="' +
              key +
              '" data-value="' +
              escapeHtml(v) +
              '">' +
              escapeHtml(label) +
              '<span aria-hidden="true">×</span></button>'
          );
        });
      });

      if (f.price) {
        chips.push(
          '<button type="button" class="smart-chip smart-chip--active" data-ss-remove-filter="price" data-value="price">' +
            escapeHtml((self.labels.facetPrice || 'Price') + ': ' + (f.price.min != null ? f.price.min : '—') + ' - ' + (f.price.max != null ? f.price.max : '—')) +
            '<span aria-hidden="true">×</span></button>'
        );
      }

      this.dom.activeChips.innerHTML = chips.join('');
      this.dom.activeChips.hidden = !chips.length;
    }

    renderToolbar(count) {
      if (!this.dom.toolbar) return;
      var self = this;
      var sorts = [
        ['relevance', this.labels.sortRelevance || 'Relevance'],
        ['price-asc', this.labels.sortPriceAsc || 'Price: low to high'],
        ['price-desc', this.labels.sortPriceDesc || 'Price: high to low'],
        ['title-asc', this.labels.sortTitle || 'Name A-Z'],
        ['newest', this.labels.sortNewest || 'Newest'],
        ['discount', this.labels.sortDiscount || 'Biggest discount']
      ];

      var correction = '';
      if (this.state.correctedFrom) {
        correction =
          '<p class="smart-search__correction">' +
          escapeHtml(this.labels.noResultsFor || 'No results for') +
          ' <em>' +
          escapeHtml(this.state.correctedFrom) +
          '</em>. ' +
          escapeHtml(this.labels.showingFor || 'Showing results for') +
          ' <strong>' +
          escapeHtml(this.state.appliedTerm) +
          '</strong>.</p>';
      }

      this.dom.toolbar.innerHTML =
        correction +
        '<div class="smart-toolbar">' +
        '<button type="button" class="smart-btn smart-btn--ghost smart-toolbar__filters" data-ss-filters-toggle>' +
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 6h16M7 12h10M10 18h4"/></svg>' +
        escapeHtml(this.labels.filters || 'Filters') +
        '</button>' +
        '<span class="smart-toolbar__count">' +
        count +
        ' ' +
        escapeHtml(count === 1 ? this.labels.resultOne || 'result' : this.labels.resultOther || 'results') +
        (this.state.approximate ? ' <em>' + escapeHtml(this.labels.approx || '(top matches)') + '</em>' : '') +
        '</span>' +
        '<label class="smart-toolbar__sort"><span>' +
        escapeHtml(this.labels.sortBy || 'Sort by') +
        '</span><select data-ss-sort>' +
        sorts
          .map(function (s) {
            return '<option value="' + s[0] + '"' + (self.state.sort === s[0] ? ' selected' : '') + '>' + escapeHtml(s[1]) + '</option>';
          })
          .join('') +
        '</select></label>' +
        '</div>';
    }

    renderFooter(totalFiltered) {
      if (!this.dom.footer) return;
      var html = '';

      if (this.state.visible < totalFiltered) {
        html +=
          '<button type="button" class="smart-btn smart-btn--outline" data-ss-load-more>' +
          escapeHtml(this.labels.loadMore || 'Load more products') +
          '</button>';
      }

      if (this.state.appliedTerm) {
        html +=
          '<a class="smart-btn smart-btn--primary" href="' +
          escapeHtml(publicSearchUrl(this.state.appliedTerm)) +
          '">' +
          escapeHtml(this.labels.viewAll || 'View all results') +
          ' (' +
          this.state.resultsCount +
          ')</a>';
      }

      this.dom.footer.innerHTML = html;
    }

    renderGroups() {
      if (!this.dom.groups) return;
      var g = this.state.groups;
      var tokens = tokenize(this.state.appliedTerm);
      var html = '';

      function list(title, items, icon) {
        if (!items || !items.length) return '';
        return (
          '<div class="smart-group"><h4>' +
          escapeHtml(title) +
          '</h4><ul>' +
          items
            .slice(0, 5)
            .map(function (it) {
              var img =
                it.image || it.featured_image
                  ? '<img src="' + escapeHtml(it.image || it.featured_image) + '" alt="" loading="lazy">'
                  : '<span class="smart-group__icon">' + icon + '</span>';
              return (
                '<li><a href="' +
                escapeHtml(it.url) +
                '">' +
                img +
                '<span>' +
                highlight(it.title, tokens) +
                '</span></a></li>'
              );
            })
            .join('') +
          '</ul></div>'
        );
      }

      if (g.queries && g.queries.length) {
        html +=
          '<div class="smart-group"><h4>' +
          escapeHtml(this.labels.suggestions || 'Suggestions') +
          '</h4><ul class="smart-group--queries">' +
          g.queries
            .slice(0, 6)
            .map(function (q) {
              return (
                '<li><button type="button" data-ss-term="' +
                escapeHtml(q.text) +
                '">' +
                (q.styled_text || highlight(q.text, tokens)) +
                '</button></li>'
              );
            })
            .join('') +
          '</ul></div>';
      }

      html += list(this.labels.collections || 'Collections', g.collections, '#');
      html += list(this.labels.pages || 'Pages', g.pages, '¶');
      html += list(this.labels.articles || 'Blog', g.articles, '✎');

      this.dom.groups.innerHTML = html;
      this.dom.groups.hidden = !html;
    }

    cardHtml(p, tokens) {
      var self = this;
      var badges = '';
      if (p.on_sale && p.discount_percent > 0) {
        badges += '<span class="smart-card__badge smart-card__badge--sale">-' + p.discount_percent + '%</span>';
      }
      if (!p.available) {
        badges += '<span class="smart-card__badge smart-card__badge--soldout">' + escapeHtml(this.labels.outStock || 'Sold out') + '</span>';
      }

      var price =
        '<span class="smart-card__price">' +
        (p.price_varies ? escapeHtml(this.labels.from || 'from') + ' ' : '') +
        escapeHtml(p.price_formatted) +
        '</span>';
      if (p.on_sale && p.compare_at_formatted) {
        price += '<s class="smart-card__compare">' + escapeHtml(p.compare_at_formatted) + '</s>';
      }

      var media = p.image
        ? '<img class="smart-card__img" src="' +
          escapeHtml(p.image) +
          '" alt="' +
          escapeHtml(p.image_alt) +
          '" loading="lazy" width="200" height="200">' +
          (p.image_hover
            ? '<img class="smart-card__img smart-card__img--hover" src="' + escapeHtml(p.image_hover) + '" alt="" loading="lazy">'
            : '')
        : '<span class="smart-card__noimg"></span>';

      var action = '';
      if (this.cfg.quickAdd !== false) {
        if (!p.available) {
          action =
            '<button type="button" class="smart-btn smart-btn--small" disabled>' +
            escapeHtml(this.labels.outStock || 'Sold out') +
            '</button>';
        } else if (p.variants_count > 1) {
          var availableVariants = (p.variants || []).filter(function (v) {
            return v.available;
          });
          action =
            '<div class="smart-card__variants"><select data-ss-variant aria-label="' +
            escapeHtml(this.labels.chooseVariant || 'Choose a variant') +
            '">' +
            availableVariants
              .map(function (v) {
                return '<option value="' + v.id + '">' + escapeHtml(v.title) + '</option>';
              })
              .join('') +
            '</select><button type="button" class="smart-btn smart-btn--small" data-ss-add data-product="' +
            p.id +
            '">' +
            escapeHtml(this.labels.addToCart || 'Add') +
            '</button></div>';
        } else {
          var v0 = (p.variants || [])[0];
          action =
            '<button type="button" class="smart-btn smart-btn--small" data-ss-add data-variant="' +
            (v0 ? v0.id : '') +
            '" data-product="' +
            p.id +
            '">' +
            escapeHtml(this.labels.addToCart || 'Add') +
            '</button>';
        }
      }

      return (
        '<article class="smart-card">' +
        '<a class="smart-card__media" href="' +
        escapeHtml(p.url) +
        '">' +
        media +
        badges +
        '</a>' +
        '<div class="smart-card__info">' +
        (p.vendor ? '<span class="smart-card__vendor">' + escapeHtml(p.vendor) + '</span>' : '') +
        '<a class="smart-card__title" href="' +
        escapeHtml(p.url) +
        '">' +
        highlight(p.title, tokens) +
        '</a>' +
        '<div class="smart-card__prices">' +
        price +
        '</div>' +
        action +
        '</div></article>'
      );
    }

    /* --------------------------------------------------------- interazione */

    handleClick(e) {
      var self = this;

      var termBtn = e.target.closest('[data-ss-term]');
      if (termBtn) {
        e.preventDefault();
        var t = termBtn.getAttribute('data-ss-term');
        this.dom.input.value = t;
        this.toggleClear();
        this.state.term = t;
        this.search(t);
        return;
      }

      var moreBtn = e.target.closest('[data-ss-facet-more]');
      if (moreBtn) {
        var key = moreBtn.getAttribute('data-ss-facet-more');
        this.state.expandedFacets[key] = !this.state.expandedFacets[key];
        this.render();
        return;
      }

      if (e.target.closest('[data-ss-clear-filters]')) {
        this.resetFilters();
        this.render();
        return;
      }

      var removeBtn = e.target.closest('[data-ss-remove-filter]');
      if (removeBtn) {
        var rkey = removeBtn.getAttribute('data-ss-remove-filter');
        var rval = removeBtn.getAttribute('data-value');
        if (rkey === 'price') {
          this.state.filters.price = null;
        } else {
          this.state.filters[rkey] = this.state.filters[rkey].filter(function (v) {
            return v !== rval;
          });
        }
        this.state.visible = this.cfg.perLoad;
        this.render();
        return;
      }

      if (e.target.closest('[data-ss-price-apply]')) {
        var minEl = this.dom.facets.querySelector('[data-ss-price="min"]');
        var maxEl = this.dom.facets.querySelector('[data-ss-price="max"]');
        var min = minEl && minEl.value !== '' ? parseFloat(minEl.value) : null;
        var max = maxEl && maxEl.value !== '' ? parseFloat(maxEl.value) : null;
        this.state.filters.price = min == null && max == null ? null : { min: min, max: max };
        this.state.visible = this.cfg.perLoad;
        this.render();
        return;
      }

      if (e.target.closest('[data-ss-load-more]')) {
        this.state.visible += this.cfg.perLoad;
        this.render();
        return;
      }

      if (e.target.closest('[data-ss-filters-toggle]')) {
        this.classList.toggle('smart-search--facets-open');
        return;
      }

      if (e.target.closest('[data-ss-facets-close]')) {
        this.classList.remove('smart-search--facets-open');
        return;
      }

      var addBtn = e.target.closest('[data-ss-add]');
      if (addBtn) {
        e.preventDefault();
        var variantId = addBtn.getAttribute('data-variant');
        if (!variantId) {
          var select = addBtn.parentElement.querySelector('[data-ss-variant]');
          variantId = select ? select.value : null;
        }
        if (variantId) this.addToCart(variantId, addBtn);
        return;
      }
    }

    handleChange(e) {
      var facetInput = e.target.closest('[data-ss-facet]');
      if (facetInput) {
        var key = facetInput.getAttribute('data-ss-facet');
        var value = facetInput.value;
        var arr = this.state.filters[key];
        if (facetInput.checked) {
          if (arr.indexOf(value) === -1) arr.push(value);
        } else {
          this.state.filters[key] = arr.filter(function (v) {
            return v !== value;
          });
        }
        this.state.visible = this.cfg.perLoad;
        this.render();
        return;
      }

      var sortSelect = e.target.closest('[data-ss-sort]');
      if (sortSelect) {
        this.state.sort = sortSelect.value;
        this.state.visible = this.cfg.perLoad;
        this.render();
      }
    }

    async addToCart(variantId, btn) {
      var self = this;
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = this.labels.adding || 'Adding…';

      try {
        var res = await fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        });
        var json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message || 'error');

        btn.textContent = this.labels.added || 'Added ✓';
        this.showToast((this.labels.addedToast || 'Product added to your cart'), true);
        this.refreshCart();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'smart-search' });
          } catch (e) {
            /* noop */
          }
        }

        setTimeout(function () {
          btn.disabled = false;
          btn.textContent = original;
        }, 1800);
      } catch (err) {
        btn.disabled = false;
        btn.textContent = original;
        this.showToast(this.labels.addError || "Couldn't add this product", false);
      }
    }

    refreshCart() {
      /* tema Naturepride: aggiorna bolla, cassetto e barra spedizione gratuita in un colpo solo */
      if (window.NP && typeof window.NP.refreshCart === 'function') {
        window.NP.refreshCart(false);
        return;
      }
      fetch(ROOT + '?sections=cart-icon-bubble')
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && data['cart-icon-bubble']) {
            var tmp = document.createElement('div');
            tmp.innerHTML = data['cart-icon-bubble'];
            var inner = tmp.querySelector('.shopify-section') || tmp;
            bubble.innerHTML = inner.innerHTML;
          }
        })
        .catch(function () {});

      var drawerSection = document.getElementById('shopify-section-cart-drawer');
      if (drawerSection) {
        fetch(ROOT + '?sections=cart-drawer')
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            if (data['cart-drawer']) drawerSection.innerHTML = data['cart-drawer'];
          })
          .catch(function () {});
      }
    }

    showToast(message, success) {
      if (!this.dom.toast) return;
      var self = this;
      this.dom.toast.className = 'smart-search__toast' + (success ? ' is-success' : ' is-error');
      this.dom.toast.innerHTML =
        escapeHtml(message) +
        ' <a href="' +
        ROOT +
        'cart">' +
        escapeHtml(this.labels.viewCart || 'View cart') +
        '</a>';
      this.dom.toast.hidden = false;
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(function () {
        self.dom.toast.hidden = true;
      }, 4000);
    }
  }

  if (!customElements.get('smart-search')) {
    customElements.define('smart-search', SmartSearchDrawer);
  }
})();
